import { yupResolver } from '@hookform/resolvers/yup';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MdTravelExplore } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchJobBoardCandidate,
  postToViewPartialCv,
  JobServeSkill,
  searchJobServe,
} from 'wisestep-api';
import { IFilterList, WsFilterTags, WsPagination } from 'wisestep-base';
import X2JS from 'x2js';
import * as yup from 'yup';

import conversationWidgetStore from '../../../features/conversationWidget/store/conversationWidgetStore';
import { ITrackCvViewJobBoards } from '../../../features/conversationWidget/utils/cvHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateCard from '../components/CandidateCard';
import NativeSearchForm from '../components/NativeSearchForm';
import BasicSearchForm from '../types/IBasicSearchForm';
import IQueryParam from '../types/IQueryParam';
import { generateJobBoardURL, getViewCVCandidateData } from '../utils/nativeSearchHelper';

import AdvancedFiltersNew from './components/AdvancedFiltersNew';
import JobServeAdvanceSearch, { AdvancedSearchKeys } from './types/IJobServeAdvanceSearch';
import JobServeCandidates from './types/JobServeCandidates';
import JobServePartialCvDetail from './types/JobServePartialCvDetail';
import JobServeResponse from './types/JobServeResponse';
import basicSearchDefaultValues from './utils/basicSearchDefaultValues';
import constructQuery from './utils/constructQuery';
import defaultAdvanceFilterValues from './utils/defaultAdvanceFilterValues';
import { processJobServeResponse } from './utils/processJobServeResponse';
import { processPartialCvDetailResponse } from './utils/processPartialCvDetailResponse';
import { pushSearchFilters } from './utils/pushSearchTags';

interface IJobServeState {
  jobServeResponse: JobServeResponse;
  partialDetailsCvMap: JobServePartialCvDetail[];
  appliedFilters: IFilterList[];
  skills: JobServeSkill[];
  openAdvanceFilter: boolean;
}

const JobServeNew = () => {
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);

  const [jobServeListingState, setJobServeListingState] = useState<IJobServeState>({
    jobServeResponse: {
      candidates: [],
    },
    partialDetailsCvMap: [],
    appliedFilters: [],
    skills: [],
    openAdvanceFilter: false,
  });
  const methods = useForm<BasicSearchForm & JobServeAdvanceSearch>({
    defaultValues: { ...basicSearchDefaultValues, ...defaultAdvanceFilterValues },
    resolver: yupResolver(
      yup.object().shape({ query: yup.string().required('Search query is required') })
    ),
  });

  const setAdvanceFilterField = (targetField: AdvancedSearchKeys, value) =>
    methods.setValue(targetField, value);
  const emptyAdvanceFieldValues = {
    ...defaultAdvanceFilterValues,
    ...{ excludeSocialOnlyProfiles: false, additionalFilters: [], contactMethod: [] },
  };
  const page = methods.watch('page');
  const accountId = methods.watch('accountId');
  const country = methods.watch('countrySelect');
  const performSearch = async (values: BasicSearchForm & JobServeAdvanceSearch) => {
    values.skills = jobServeListingState.skills;
    setJobServeListingState((prev) => ({
      ...prev,
      appliedFilters: pushSearchFilters(values),
    }));
    try {
      const data = await searchJobServe(constructQuery(values));
      if (data.data && (data.data.statusCode == '400' || data.data.statusCode == '504')) {
        const errorMessage = data.data.status as string;
        setNotification(failed(errorMessage));
        return;
      }
      const x2js = new X2JS();
      const document = x2js.xml2js(data.data);
      const response = processJobServeResponse(document);
      setJobServeListingState((prev) => ({ ...prev, jobServeResponse: response }));
    } catch (e) {
      console.log('error', e);
    }
  };
  const updateSkillState = (skills: JobServeSkill[]) =>
    setJobServeListingState((prev) => ({
      ...prev,
      skills: skills,
    }));
  useEffect(() => {
    if (methods.formState.errors.query?.message) {
      setNotification(failed('Search Query is Required'));
    }
  }, [methods.formState.errors.query, setNotification]);

  const isDownloaded = (candidate: JobServeCandidates): boolean =>
    jobServeListingState.jobServeResponse.existing?.some(
      (existingCandidate) =>
        existingCandidate.candidateJobBoardId === candidate.CandidateID + '_' + candidate.CVID
    ) ?? false;

  const onDeleteFilterTag = (tag: string) => {
    const idOfChip =
      jobServeListingState.appliedFilters.find((existChip) => existChip.tag === tag)?.id ?? '';
    if (!isEmpty(idOfChip)) {
      setAdvanceFilterField(idOfChip as AdvancedSearchKeys, emptyAdvanceFieldValues[idOfChip]);
      methods.handleSubmit(performSearch)();
      const index = jobServeListingState.appliedFilters.findIndex(
        (existChip) => existChip.id === idOfChip
      );
      jobServeListingState.appliedFilters.splice(index, 1);
      setJobServeListingState((prev) => ({
        ...prev,
        appliedFilters: [...jobServeListingState.appliedFilters],
      }));
    }
  };

  const getSkills = (CVID: string) => {
    let candidateSkills: string[] =
      jobServeListingState.partialDetailsCvMap
        .find((sk) => sk.candidateId === CVID)
        ?.partialCvDetail?.Skills?.split('</li><li>') ?? [];

    candidateSkills = candidateSkills.map((sk) =>
      sk.replace('</ul>', ',').replace('<ul>', '').replace('<li>', '').replace('</li>', '')
    );
    return candidateSkills;
  };

  const hasPartialDetailsCv = (CVID: string) => {
    return jobServeListingState.partialDetailsCvMap.some(
      (existChip) => existChip.candidateId === CVID
    );
  };

  const getDetailItems = (candidate: JobServeCandidates) => {
    const detailItems = [
      {
        icon: <MdTravelExplore color="#888A8E" />,
        label: `${candidate.PreferredLocations ? candidate.PreferredLocations : ''}`,
        tooltip: 'Preferred Job Location',
      },
      {
        icon: <LocationOnIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
        label: `${candidate.Residency ? candidate.Residency : ''}`,
        tooltip: 'Candidate Location',
      },
      {
        icon: <WorkIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
        label: `${
          candidate.candidateData.currentJobTitle ? candidate.candidateData.currentJobTitle : ''
        }`,
        tooltip: 'Current Job Title',
      },
    ];
    const partialCvDetail = jobServeListingState.partialDetailsCvMap.find(
      (sk) => sk.candidateId === candidate.CVID
    )?.partialCvDetail;
    const prefferedRole = partialCvDetail?.RoleSought ?? '';
    const availability = partialCvDetail?.Availability ?? '';
    if (prefferedRole && !isEmpty(prefferedRole)) {
      detailItems.push({
        icon: <PeopleIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
        label: `${prefferedRole}`,
        tooltip: 'Preferred Role',
      });
    }
    if (availability && !isEmpty(availability)) {
      detailItems.push({
        icon: <AccessTimeFilledIcon sx={{ color: '#888A8E', fontSize: '1.2rem' }} />,
        label: `${availability}`,
        tooltip: 'Availability',
      });
    }
    return detailItems;
  };

  const onViewCvClick = async (candidateId: string, candidate: JobServeCandidates) => {
    try {
      const candidateData = await fetchJobBoardCandidate({
        jobBoardAccountId: accountId,
        jobBoardId: candidate.CandidateID + '_' + candidateId,
        force: false,
        harvestingId: '',
        candidateData: {
          ...candidate.candidateData,
          jobId: selectedJob?.id ?? '',
          team: selectedJob?.activeWorkAssignment?.team ?? null,
        },
      });
      const params: IQueryParam = {
        resumeUpdatedOn: candidate.UpdatedDate,
        candidate: getEncodedQueryParams(getViewCVCandidateData(candidate.candidateData)),
        accountId: accountId,
        jobBoardId: candidate.CandidateID + '_' + candidateId,
        query: encodeURI(methods.getValues('query') ?? ''),
        jobBoard: 'JobServe',
      };
      setCvViewSourcePage(ITrackCvViewJobBoards.JOBSERVESEARCH);
      const paramId = `param${uuidv4()}`;
      localStorage.setItem(paramId, getEncodedQueryParams(params));
      openUrlInNewTab(
        `/candidate-layout/profile?id=${
          candidateData?.newCandidateData?.id ?? candidateData?.candidateId
        }&array=${[]}&q=${getEncodedQueryParams(params)}&backwardpathArray=${getEncodedQueryParams(
          JSON.stringify(generateJobBoardURL())
        )}`
      );
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.data.status) {
        setNotification(failed(err.response?.data.status));
      } else {
        setNotification(failed('There was some problem in fetching the candidate.'));
      }
    }
  };

  const onViewDetailsClick = async (candidateId: string, candidate: JobServeCandidates) => {
    try {
      const candidateDataXml = await postToViewPartialCv({
        jobBoardAccountId: accountId,
        jobBoardId: candidate.CandidateID + '_' + candidateId,
        harvestingId: '',
      });
      const x2js = new X2JS();
      const candidateData = x2js.xml2js(candidateDataXml.data);
      const partialCvDetail = processPartialCvDetailResponse(candidateData);
      setJobServeListingState((prev) => ({
        ...prev,
        partialDetailsCvMap: [...jobServeListingState.partialDetailsCvMap, partialCvDetail],
      }));
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.data.status) {
        setNotification(failed(err.response?.data.status));
      } else {
        setNotification(failed('There was some problem in fetching the partial cv details .'));
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <Grid container display="flex" justifyContent="space-between">
        <Grid item xs={9}>
          <Box ml={2}>
            <NativeSearchForm
              jobBoardName="JOB_SERVE"
              performSearch={performSearch}
              country={country}
            />
          </Box>
          <WsFilterTags
            filtersList={jobServeListingState.appliedFilters}
            onDelete={onDeleteFilterTag}
          />
          {jobServeListingState.jobServeResponse.totalItems &&
            jobServeListingState.jobServeResponse.totalItems > 0 && (
              <WsPagination
                count={Math.ceil(jobServeListingState.jobServeResponse.totalItems / 50)}
                onChange={(_, value) => {
                  methods.setValue('page', value + 1);
                  methods.handleSubmit(performSearch)();
                }}
                itemsPerPage={50}
                pageNumber={page - 1}
                defaultPage={page}
                isPageCount={true}
                type="Candidates"
                totalCount={jobServeListingState.jobServeResponse.totalItems}
              />
            )}
          {jobServeListingState.jobServeResponse?.candidates.map((candidate) => (
            <Grid my={2} key={candidate.CVID} item xs={12}>
              <CandidateCard
                candidateId={candidate.CVID}
                title={candidate.candidateData.name ?? ''}
                name={candidate.candidateData.name ?? ''}
                imgSrc={''}
                profileDescription={candidate.CandidateProfile}
                isDownloaded={isDownloaded(candidate)}
                skills={getSkills(candidate.CVID)}
                dateLastActive={'' + candidate.LastApplication}
                dateResumeLastUpdated={candidate.UpdatedDate}
                isViewCv={hasPartialDetailsCv(candidate.CVID)}
                viewCvLabel={'View full profile'}
                onViewCvClick={async (candidateId) => onViewCvClick(candidateId, candidate)}
                onViewDetailsClick={async (candidateId) =>
                  onViewDetailsClick(candidateId, candidate)
                }
                detailItems={getDetailItems(candidate)}
              />
            </Grid>
          ))}
        </Grid>
        <Grid item xs={2.8}>
          <AdvancedFiltersNew
            performSearch={performSearch}
            country={country}
            setSkills={updateSkillState}
            skills={jobServeListingState.skills}
          />
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default JobServeNew;
